import { Component, Input, Output, EventEmitter, OnInit, inject, ViewChild, ElementRef } from '@angular/core';
import { FileStorageService } from 'src/app/services/file-storage.service';
import { LogError, LogStatus } from '@memberjunction/core';
import { FileRecord } from 'src/app/utils/interfaces';

export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  errorMessage?: string;
  category: string;
  description: string;
  tags?: string;
  isFavorite: boolean;
}

export interface DocumentUploadData {
  files: UploadedFile[];
  defaultCategory: string;
  uploadResults?: any[];
}

@Component({
  selector: 'acs-document-upload-modal',
  templateUrl: './acs-document-upload-modal.html',
  styleUrls: ['./acs-document-upload-modal.scss']
})
export class AcsDocumentUploadModalComponent implements OnInit {
  private fileStorageService = inject(FileStorageService);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @Output() modalClosed = new EventEmitter<void>();
  @Output() documentsUploaded = new EventEmitter<DocumentUploadData>();

  currentStep = 1;
  totalSteps = 2;
  isUploading = false;
  isDragOver = false;

  uploadData: DocumentUploadData = {
    files: [],
    defaultCategory: 'general-documents'  // Use Name field from database
  };

  // Will be populated from database
  documentCategories: Array<{ value: string; label: string; icon: string; description: string }> = [];

  // User documents are always private - no access levels needed

  allowedFileTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/gif'
  ];

  maxFileSize = 10 * 1024 * 1024; // 10MB
  maxTotalFiles = 20;

  ngOnInit(): void {
    this.initializeUpload();
    this.loadFileCategories();
  }

  private loadFileCategories(): void {
    // Get categories from FileStorageService (now returns structured category data)
    this.fileStorageService.getFileCategories().subscribe({
      next: (categories) => {
        this.documentCategories = categories.map(category => {
          // Map categories to display objects with icons
          const iconMap: { [key: string]: string } = {
            'Meeting Minutes': '📝',
            'Meeting Agendas': '📅',
            'Financial Reports': '💰',
            'Invoices & Receipts': '🧾',
            'Bylaws & Policies': '📋',
            'Forms & Applications': '📑',
            'Event Materials': '🎉',
            'Educational Content': '🎓',
            'Announcements': '📢',
            'General Documents': '📄'
          };
          
          // Use the Name field as the value (stable identifier)
          // Display the DisplayName to the user
          return {
            value: category.name,  // Use Name field (e.g., "meeting-minutes") 
            label: category.displayName,  // Show DisplayName (e.g., "Meeting Minutes")
            icon: iconMap[category.displayName] || '📄',
            description: category.description
          };
        });
        
        // Set default category if categories loaded successfully
        if (this.documentCategories.length > 0) {
          // Find general-documents or use first category as fallback
          const generalCategory = this.documentCategories.find(cat => cat.value === 'general-documents');
          this.uploadData.defaultCategory = generalCategory?.value || this.documentCategories[0].value;
        }
      },
      error: (error) => {
        LogError('Failed to load document categories:', undefined, error);
        // Use fallback categories with Name field as value
        this.documentCategories = [
          { value: 'general-documents', label: 'General Documents', icon: '📄', description: 'Miscellaneous chapter documents' },
          { value: 'meeting-minutes', label: 'Meeting Minutes', icon: '📝', description: 'Official records of chapter meetings' },
          { value: 'meeting-agendas', label: 'Meeting Agendas', icon: '📅', description: 'Upcoming meeting agendas and materials' },
          { value: 'financial-reports', label: 'Financial Reports', icon: '💰', description: 'Budget reports, financial statements, and treasurer documents' },
          { value: 'invoices-receipts', label: 'Invoices & Receipts', icon: '🧾', description: 'Payment records and financial transactions' },
          { value: 'bylaws-policies', label: 'Bylaws & Policies', icon: '📋', description: 'Chapter governing documents and policies' },
          { value: 'forms-applications', label: 'Forms & Applications', icon: '📑', description: 'Membership forms, applications, and official documents' },
          { value: 'event-materials', label: 'Event Materials', icon: '🎉', description: 'Event planning documents, flyers, and promotional materials' },
          { value: 'educational-content', label: 'Educational Content', icon: '🎓', description: 'Training materials, presentations, and educational resources' },
          { value: 'announcements', label: 'Announcements', icon: '📢', description: 'Chapter announcements and communications' }
        ];
      }
    });
  }
  
  private getCategoryDescription(category: string): string {
    const descriptions: { [key: string]: string } = {
      'Meeting Minutes': 'Official records of chapter meetings',
      'Meeting Agendas': 'Upcoming meeting agendas and materials',
      'Financial Reports': 'Budget reports, financial statements, and treasurer documents',
      'Invoices & Receipts': 'Payment records and financial transactions',
      'Bylaws & Policies': 'Chapter governing documents and policies',
      'Forms & Applications': 'Membership forms, applications, and official documents',
      'Event Materials': 'Event planning documents, flyers, and promotional materials',
      'Educational Content': 'Training materials, presentations, and educational resources',
      'Announcements': 'Chapter announcements and communications',
      'General Documents': 'Miscellaneous chapter documents'
    };
    return descriptions[category] || `${category} documents and materials`;
  }

  initializeUpload(): void {
    this.uploadData = {
      files: [],
      defaultCategory: 'general-documents'  // Use Name field from database
    };
  }

  onModalClose(): void {
    this.currentStep = 1;
    this.isUploading = false;
    this.isDragOver = false;
    this.initializeUpload();
    this.close.emit();
    this.modalClosed.emit();
  }

  canProceedToStep(step: number): boolean {
    switch (step) {
      case 2:
        return this.uploadData.files.length > 0 &&
               this.uploadData.files.every(f => f.status !== 'error');
      default:
        return true;
    }
  }

  nextStep(): void {
    if (this.currentStep < this.totalSteps && this.canProceedToStep(this.currentStep + 1)) {
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step <= this.currentStep || this.canProceedToStep(step)) {
      this.currentStep = step;
    }
  }

  // File Upload Handling
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = Array.from(event.dataTransfer?.files || []);
    this.handleFiles(files);
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    this.handleFiles(files);
    input.value = ''; // Reset input
  }

  openFileDialog(): void {
    this.fileInput.nativeElement.click();
  }

  handleFiles(files: File[]): void {
    const validFiles = files.filter(file => this.validateFile(file));
    
    if (this.uploadData.files.length + validFiles.length > this.maxTotalFiles) {
      alert(`Maximum ${this.maxTotalFiles} files allowed. Please remove some files first.`);
      return;
    }

    validFiles.forEach(file => {
      const uploadFile: UploadedFile = {
        id: this.generateFileId(),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        status: 'pending',
        category: this.uploadData.defaultCategory,
        description: '',
        tags: '',
        isFavorite: false
      };
      
      this.uploadData.files.push(uploadFile);
    });
  }

  validateFile(file: File): boolean {
    // Check file type
    if (!this.allowedFileTypes.includes(file.type)) {
      alert(`File type ${file.type} is not allowed. Please select a supported file type.`);
      return false;
    }

    // Check file size
    if (file.size > this.maxFileSize) {
      alert(`File ${file.name} is too large. Maximum size is ${this.formatFileSize(this.maxFileSize)}.`);
      return false;
    }

    // Check for duplicates
    if (this.uploadData.files.some(f => f.name === file.name)) {
      alert(`File ${file.name} is already selected.`);
      return false;
    }

    return true;
  }

  generateFileId(): string {
    return 'file_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  removeFile(fileId: string): void {
    this.uploadData.files = this.uploadData.files.filter(f => f.id !== fileId);
  }

  updateFileProperty(fileId: string, property: keyof UploadedFile, value: any): void {
    const file = this.uploadData.files.find(f => f.id === fileId);
    if (file) {
      (file as any)[property] = value;
    } else {
      LogError('File not found for property update:', undefined, { fileId, property, value });
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(file: UploadedFile): string {
    if (file.type.includes('pdf')) return '📄';
    if (file.type.includes('word') || file.type.includes('document')) return '📝';
    if (file.type.includes('excel') || file.type.includes('spreadsheet')) return '📊';
    if (file.type.includes('powerpoint') || file.type.includes('presentation')) return '📋';
    if (file.type.includes('image')) return '🖼️';
    if (file.type.includes('text')) return '📃';
    return '📎';
  }

  // User documents don't need chapter selection - they belong to current user

  // Bulk Operations
  applyBulkCategory(category: string): void {
    this.uploadData.files.forEach(file => {
      file.category = category;
    });
  }

  applyBulkFavorite(isFavorite: boolean): void {
    this.uploadData.files.forEach(file => {
      file.isFavorite = isFavorite;
    });
  }

  applyBulkTags(tags: string): void {
    this.uploadData.files.forEach(file => {
      file.tags = tags;
    });
  }

  // Upload Process - User Document Upload
  async uploadDocuments(): Promise<void> {
    if (!this.canUpload()) return;

    this.isUploading = true;
    const uploadResults: FileRecord[] = [];
    let hasErrors = false;

    try {
      // Process each file upload for user's personal repository
      for (const uploadedFile of this.uploadData.files) {
        uploadedFile.status = 'uploading';
        uploadedFile.progress = 0;
        uploadedFile.errorMessage = undefined;

        try {
          LogStatus(`Uploading ${uploadedFile.file.name} with category: "${uploadedFile.category}"`);
          const fileRecord = await this.uploadFileWithProgress(
            uploadedFile.file, 
            uploadedFile.category,
            uploadedFile.description,
            (progress) => {
              uploadedFile.progress = progress;
            },
            uploadedFile.tags,
            uploadedFile.isFavorite
          );
          uploadResults.push(fileRecord);

          uploadedFile.status = 'completed';
          uploadedFile.progress = 100;
        } catch (error) {
          hasErrors = true;
          LogError(`Failed to upload ${uploadedFile.name}:`, undefined, error);
          uploadedFile.status = 'error';
          uploadedFile.progress = 0;
          
          // Provide specific error messages based on error type
          if (error instanceof Error) {
            if (error.message.includes('Azure')) {
              uploadedFile.errorMessage = 'Storage upload failed - please check your connection and try again';
            } else if (error.message.includes('create')) {
              uploadedFile.errorMessage = 'Failed to create document record - please check permissions';
            } else if (error.message.includes('URL')) {
              uploadedFile.errorMessage = 'Failed to get upload authorization - please try again';
            } else {
              uploadedFile.errorMessage = error.message;
            }
          } else {
            uploadedFile.errorMessage = 'Unknown error occurred during upload';
          }
        }
      }

      // Emit the upload data with results
      this.documentsUploaded.emit({ 
        ...this.uploadData,
        uploadResults: uploadResults
      });
      
      // Only close modal if no errors occurred (allow user to retry failed uploads)
      if (!hasErrors) {
        this.onModalClose();
      }
    } catch (error) {
      LogError('Document upload process failed:', undefined, error);
      // Keep modal open to show error state
    } finally {
      this.isUploading = false;
    }
  }

  /**
   * Upload a single file to user's personal document repository
   * Uses GraphQL DocumentOperationsResolver for complex operations
   */
  private async uploadFileWithProgress(
    file: File, 
    category: string,
    description: string,
    onProgress: (progress: number) => void,
    tags?: string,
    isFavorite?: boolean
  ): Promise<FileRecord> {
    try {
      onProgress(10); // Starting upload

      // Step 1: Create user document record and get secure Azure upload URL
      const createResponse = await this.fileStorageService.createFile({
        Name: file.name,
        ProviderID: 'azure-blob-provider',
        Status: 'Pending',
        Category: category,
        Description: description,
        Tags: tags,
        IsFavorite: isFavorite,
        ContentType: file.type,
        Size: file.size
      }).toPromise();

      if (!createResponse?.success || !createResponse.file || !createResponse.uploadUrl) {
        throw new Error(createResponse?.file ? 'Failed to get upload URL' : 'Failed to create document record');
      }

      onProgress(30); // Document record created, upload URL obtained

      // Step 2: Upload directly to Azure Storage using pre-authenticated URL
      const uploadResponse = await this.fileStorageService.uploadFileToStorage(
        createResponse.uploadUrl, 
        file
      ).toPromise();

      if (!uploadResponse?.success) {
        throw new Error(uploadResponse?.error || 'Failed to upload to Azure Storage');
      }

      onProgress(90); // Upload completed successfully

      // Step 3: Log the upload access for audit trail
      await this.fileStorageService.logDocumentAccess(
        createResponse.file.ID,
        'Upload'
      );

      onProgress(100); // Process completed

      // Return the file record (status managed automatically by DocumentOperationsResolver)
      return {
        ...createResponse.file,
        Status: 'Active',
        DownloadUrl: '' // Will be generated on-demand via getDocumentDownloadUrl
      };

    } catch (error) {
      LogError('File upload failed:', undefined, error);
      throw error;
    }
  }

  canUpload(): boolean {
    return this.uploadData.files.length > 0 &&
           !this.isUploading;
  }

  /**
   * Retry failed uploads
   */
  async retryFailedUploads(): Promise<void> {
    const failedFiles = this.uploadData.files.filter(f => f.status === 'error');
    
    if (failedFiles.length === 0) return;

    this.isUploading = true;
    const uploadResults: FileRecord[] = [];

    try {
      for (const uploadedFile of failedFiles) {
        uploadedFile.status = 'uploading';
        uploadedFile.progress = 0;
        uploadedFile.errorMessage = undefined;

        try {
          const fileRecord = await this.uploadFileWithProgress(
            uploadedFile.file, 
            uploadedFile.category,
            uploadedFile.description,
            (progress) => {
              uploadedFile.progress = progress;
            },
            uploadedFile.tags,
            uploadedFile.isFavorite
          );
          uploadResults.push(fileRecord);

          uploadedFile.status = 'completed';
          uploadedFile.progress = 100;
        } catch (error) {
          LogError(`Retry failed for ${uploadedFile.name}:`, undefined, error);
          uploadedFile.status = 'error';
          uploadedFile.progress = 0;
          uploadedFile.errorMessage = error instanceof Error ? error.message : 'Retry failed';
        }
      }

      // Check if all uploads are now complete
      const hasAnyErrors = this.uploadData.files.some(f => f.status === 'error');
      if (!hasAnyErrors) {
        this.documentsUploaded.emit({ 
          ...this.uploadData,
          uploadResults: uploadResults
        });
        this.onModalClose();
      }
    } catch (error) {
      LogError('Retry process failed:', undefined, error);
    } finally {
      this.isUploading = false;
    }
  }

  /**
   * Get count of files in different states
   */
  getUploadStats() {
    const total = this.uploadData.files.length;
    const completed = this.uploadData.files.filter(f => f.status === 'completed').length;
    const failed = this.uploadData.files.filter(f => f.status === 'error').length;
    const uploading = this.uploadData.files.filter(f => f.status === 'uploading').length;
    
    return { total, completed, failed, uploading };
  }

  getTotalFileSize(): number {
    return this.uploadData.files.reduce((total, file) => total + file.size, 0);
  }

  getEstimatedUploadTime(): string {
    const totalSize = this.getTotalFileSize();
    const avgSpeed = 1024 * 1024; // 1 MB/s average upload speed
    const seconds = Math.ceil(totalSize / avgSpeed);
    
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.ceil(seconds / 60)}m`;
    return `${Math.ceil(seconds / 3600)}h`;
  }

  getCategoryOption(value: string) {
    return this.documentCategories.find(cat => cat.value === value);
  }

  // Access levels removed - user documents are always private
}